/** Visual estimate based on the plan data supplied by Shopify's payment_terms. */
class FnkInstallments extends HTMLElement {
  static observedAttributes = ['data-price'];

  #observer = new MutationObserver(() => this.#sync());
  #change = () => this.#sync();

  connectedCallback() {
    this.closest('form')?.addEventListener('change', this.#change);
    this.#sync();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.closest('form')?.removeEventListener('change', this.#change);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#sync();
  }

  #sync() {
    this.#observer.disconnect();
    const panel = this.querySelector('.fnk-installments__plan');
    const nativeTerms = this.querySelector('shopify-payment-terms');
    let offer;
    try {
      const meta = JSON.parse(nativeTerms?.getAttribute('shopify-meta') || 'null');
      const variantId = this.closest('form')?.querySelector('input[name="id"]')?.value;
      const variant = meta?.type === 'product'
        ? meta.variants?.find(item => String(item.id) === variantId)
        : meta;
      const total = Number(this.dataset.price);
      // Require an eligible matching offer. Never infer interest-free terms by price alone.
      if (meta && variant?.eligible && variant.available !== false && total > 0 &&
          Math.round(Number(variant.full_price_amount) * 100) === total) {
        const plan = meta.financing_plans?.find(item =>
          total >= Math.round(item.min_price_amount * 100) &&
          total <= Math.round(item.max_price_amount * 100)
        );
        const count = Number(variant.number_of_payment_terms);
        const term = plan?.terms?.find(item =>
          item.loan_type === 'split_pay' && item.apr === 0 && item.installments_count === count
        );
        if (term && count >= 2 && count <= 4) {
          const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency', currency: meta.currency_code
          });
          const amount = Number(variant.price_per_term_amount);
          if (amount > 0 && Math.abs(amount * 100 * count - total) <= count) {
            offer = { count, formatted: formatter.format(amount) };
          }
        }
      }
    } catch {
      // Keep Shopify's original banner if its plan data is unavailable or changes shape.
    }

    if (panel) {
      panel.hidden = !offer;
      this.toggleAttribute('data-has-plan', Boolean(offer));
      if (offer) {
        this.querySelector('[data-installment-amount]').textContent = offer.formatted;
        const schedule = this.querySelector('.fnk-installments__schedule');
        schedule.style.setProperty('--installment-count', String(offer.count));
        schedule.replaceChildren(...Array.from({ length: offer.count }, (_, index) => {
          const item = document.createElement('li');
          item.className = 'fnk-installments__payment';
          const amount = document.createElement('strong');
          amount.textContent = offer.formatted;
          const label = document.createElement('span');
          label.textContent = ['To start', '2nd payment', '3rd payment', '4th payment'][index];
          item.append(amount, label);
          return item;
        }));
      }
    }
    this.#observer.observe(this, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['shopify-meta', 'variant-id', 'value']
    });
  }
}

if (!customElements.get('fnk-installments')) customElements.define('fnk-installments', FnkInstallments);
